import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Recycle, Building2, ChevronRight, ArrowLeft, FileText, Download } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, API } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import FirModal from "@/components/FirModal";

export default function Fir() {
  const [cantieri, setCantieri] = useState([]);
  const [selectedCantiere, setSelectedCantiere] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => { api.get("/cantieri").then(setCantieri).catch(() => {}); }, []);

  const load = useCallback(async () => {
    if (!selectedCantiere) { setItems([]); return; }
    setLoading(true);
    try { setItems(await api.get("/fir", { cantiere_id: selectedCantiere })); }
    finally { setLoading(false); }
  }, [selectedCantiere]);

  useEffect(() => { load(); }, [load]);

  const confirmDelete = async () => {
    try {
      await api.del(`/fir/${toDelete.id}`);
      toast.success("FIR eliminato.");
      setToDelete(null); load();
    } catch (err) { toast.error(err.message); }
  };

  const selCant = cantieri.find((c) => c.id === selectedCantiere);

  return (
    <div className="space-y-6">
      {!selectedCantiere ? (
        <>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Formulari Rifiuti (FIR)</h1>
            <p className="text-sm text-muted-foreground mt-1">Seleziona un cantiere per vedere e gestire i suoi formulari di identificazione rifiuti.</p>
          </div>
          {cantieri.length === 0 ? (
            <Card><CardContent className="py-16 text-center">
              <Building2 className="mx-auto h-10 w-10 text-muted-foreground" />
              <p className="mt-4 font-medium">Nessun cantiere</p>
              <p className="text-sm text-muted-foreground">Crea prima un cantiere in "Cantieri &amp; Commesse".</p>
            </CardContent></Card>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {cantieri.map((c) => (
                <button key={c.id} data-testid={`fir-select-cantiere-${c.id}`} onClick={() => setSelectedCantiere(c.id)}
                  className="group flex items-center justify-between gap-3 rounded-xl border-2 border-border bg-card px-5 py-5 text-left transition-all hover:border-amber-400 hover:bg-amber-50/40 dark:hover:bg-amber-950/10 animate-fade-up">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-amber-500/15 text-amber-600 shrink-0"><Recycle className="h-5 w-5" /></div>
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{c.nome}</p>
                      <p className="text-xs text-muted-foreground truncate">{c.codice_commessa ? `${c.codice_commessa} · ` : ""}{c.cliente || "—"}</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-amber-600 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" data-testid="fir-back-button" onClick={() => setSelectedCantiere("")}><ArrowLeft className="h-4 w-4" /></Button>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{selCant?.nome || "Formulari Rifiuti"}</h1>
                <p className="text-sm text-muted-foreground mt-1">Formulari di Identificazione Rifiuti (FIR) della commessa.</p>
              </div>
            </div>
            <Button onClick={() => { setEditing(null); setModal(true); }} data-testid="add-fir-button"
              className="gap-2 bg-slate-900 hover:bg-slate-800 dark:bg-amber-500 dark:hover:bg-amber-600 dark:text-slate-900">
              <Plus className="h-4 w-4" /> Nuovo FIR
            </Button>
          </div>

          {loading ? (
            <div className="h-48 rounded-xl bg-secondary animate-pulse" />
          ) : items.length === 0 ? (
            <Card><CardContent className="py-16 text-center">
              <Recycle className="mx-auto h-10 w-10 text-muted-foreground" />
              <p className="mt-4 font-medium">Nessun formulario</p>
              <p className="text-sm text-muted-foreground">Aggiungi il primo FIR per questo cantiere e carica il PDF.</p>
            </CardContent></Card>
          ) : (
            <Card><CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Numero</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>CER</TableHead>
                    <TableHead>Destinatario</TableHead>
                    <TableHead>Quantità</TableHead>
                    <TableHead>PDF</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((f) => (
                    <TableRow key={f.id} data-testid={`fir-row-${f.id}`}>
                      <TableCell className="font-medium">{f.numero || "—"}</TableCell>
                      <TableCell>{f.data ? fmtDate(f.data) : "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{f.codice_cer || "—"}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{f.destinatario || "—"}</TableCell>
                      <TableCell>{f.quantita || "—"}</TableCell>
                      <TableCell>
                        {f.file_id ? (
                          <a href={`${API}/uploads/${f.file_id}?download=1`} target="_blank" rel="noreferrer"
                            data-testid={`fir-pdf-${f.id}`} className="inline-flex items-center gap-1 text-amber-600 hover:underline text-sm">
                            <FileText className="h-4 w-4" /> Apri
                          </a>
                        ) : <span className="text-muted-foreground text-sm">—</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-fir-${f.id}`} onClick={() => { setEditing(f); setModal(true); }}><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" data-testid={`delete-fir-${f.id}`} onClick={() => setToDelete(f)}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          )}
        </>
      )}

      <FirModal open={modal} onOpenChange={setModal} cantiereId={selectedCantiere} initial={editing} onSaved={load} />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo FIR?</AlertDialogTitle>
            <AlertDialogDescription>L'operazione rimuoverà il formulario dall'elenco della commessa.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
